import React from 'react';
import { Button } from '@components/primitives/Button';
import {
  setEditingAppointmentId,
  useRejectAppointmentMutation,
  useUpdateAppointmentMutation,
} from '@features/appointments';
import { formatAppointmentApprovedNotification } from '@features/appointments/utils/appointmentNotifications';
import { Box } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { useSnackbar } from 'notistack';

import type { CalendarEvent } from '../../../types';

import { ActionButtonsRow } from './ActionButtons.styles';

/**
 * Props for ActionButtons component
 */
export interface ActionButtonsProps {
  /** Calendar event data */
  event: CalendarEvent;
  /** Close modal handler */
  onClose: () => void;
  /** Calendar view mode (for analytics) */
  viewMode?: string;
  /** Whether device is touch-enabled */
  isTouch: boolean;
}

/**
 * ActionButtons - Renders action buttons based on event type
 * Displayed between MainContentRow and ActionFooter in modal mode
 */
export const ActionButtons: React.FC<ActionButtonsProps> = ({
  event,
  onClose,
  viewMode,
  isTouch,
}) => {
  const resource = event.resource;
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  // RTK Query mutations
  const [updateAppointment, { isLoading: isApproving }] = useUpdateAppointmentMutation();
  const [rejectAppointment, { isLoading: isRejecting }] = useRejectAppointmentMutation();

  // Combine all loading states to prevent race conditions
  // When any mutation is in progress, disable all action buttons
  const isLoading = isApproving || isRejecting;

  // Analytics tracking
  const { appointment: appointmentTracking } = useEventTracking();

  // Helper to get actionable event type for analytics
  const getActionableEventType = (): 'suggested' | 'cancelled' | 'reschedule_requested' | null => {
    if (resource.status === 'suggested') return 'suggested';
    if (resource.confirmation_status === 'cancelled') return 'cancelled';
    if (resource.confirmation_status === 'reschedule_requested') return 'reschedule_requested';
    return null;
  };

  // Handle approve action - accepts the suggested appointment as-is
  const handleApprove = async () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'approve',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    try {
      const updatedAppointment = await updateAppointment({
        appointmentId: resource.appointment_id,
        appointmentUpdate: {}, // Empty payload accepts appointment with current values
      }).unwrap();

      // Track completion success
      if (actionableType) {
        appointmentTracking.eventQuickActionCompleted({
          appointment_id: resource.appointment_id,
          actionable_event_type: actionableType,
          action: 'approve',
          success: true,
        });
      }

      // Show success notification
      const notificationData = formatAppointmentApprovedNotification(updatedAppointment);
      enqueueSnackbar('Appointment Approved!', {
        variant: 'success',
        data: notificationData,
      });

      onClose();
    } catch (error) {
      console.error('Failed to approve appointment:', error);

      // Track completion failure
      if (actionableType) {
        appointmentTracking.eventQuickActionCompleted({
          appointment_id: resource.appointment_id,
          actionable_event_type: actionableType,
          action: 'approve',
          success: false,
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      enqueueSnackbar('Failed to approve appointment. Please try again.', {
        variant: 'error',
      });
    }
  };

  // Handle edit action - dispatches Redux action to open edit modal
  const handleEdit = () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'edit',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    dispatch(setEditingAppointmentId(resource.appointment_id));
    onClose();
  };

  // Handle remove action - rejects the suggested appointment
  const handleRemove = async () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'remove',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    try {
      await rejectAppointment({
        appointmentId: resource.appointment_id,
        appointmentReject: {}, // Empty payload as per API spec
      }).unwrap();

      // Track completion success
      if (actionableType) {
        appointmentTracking.eventQuickActionCompleted({
          appointment_id: resource.appointment_id,
          actionable_event_type: actionableType,
          action: 'remove',
          success: true,
        });
      }

      // Show success notification
      enqueueSnackbar('Suggested Appointment Rejected', {
        variant: 'error',
      });

      onClose();
    } catch (error) {
      console.error('Failed to reject appointment:', error);

      // Track completion failure
      if (actionableType) {
        appointmentTracking.eventQuickActionCompleted({
          appointment_id: resource.appointment_id,
          actionable_event_type: actionableType,
          action: 'remove',
          success: false,
          error_message: error instanceof Error ? error.message : 'Unknown error',
        });
      }

      enqueueSnackbar('Failed to reject appointment. Please try again.', {
        variant: 'error',
      });
    }
  };

  // Handle deny action
  const handleDeny = () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'deny',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    // TODO: Implement deny logic (deny reschedule request)
    console.log('Deny clicked for appointment:', resource.appointment_id);
    onClose();
  };

  // Handle contact action
  const handleContact = () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'contact',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    // TODO: Implement contact logic (open messaging interface)
    console.log('Contact clicked for appointment:', resource.appointment_id);
    onClose();
  };

  // Handle acknowledge action
  const handleAcknowledge = () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'acknowledge',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    // TODO: Implement acknowledge logic (mark as acknowledged)
    console.log('Acknowledge clicked for appointment:', resource.appointment_id);
    onClose();
  };

  // Handle re-book action
  const handleRebook = () => {
    const actionableType = getActionableEventType();

    // Track click
    if (actionableType) {
      appointmentTracking.eventQuickActionClicked({
        appointment_id: resource.appointment_id,
        actionable_event_type: actionableType,
        action: 'rebook',
        view_mode: (viewMode as 'month' | 'week' | 'day') || 'month',
        is_touch_device: isTouch,
      });
    }

    // TODO: Implement re-book logic (create new appointment)
    console.log('Re-book clicked for appointment:', resource.appointment_id);
    onClose();
  };

  // Determine button set based on event properties
  // Suggested appointment (provider/system suggested)
  if (resource.status === 'suggested') {
    return (
      <ActionButtonsRow sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="text"
          color="primary"
          onClick={handleRemove}
          size="medium"
          disabled={isLoading}
        >
          {isRejecting ? 'Removing...' : 'Remove'}
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleEdit}
            size="medium"
            disabled={isLoading}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleApprove}
            size="medium"
            disabled={isLoading}
          >
            {isApproving ? 'Accepting...' : 'Accept'}
          </Button>
        </Box>
      </ActionButtonsRow>
    );
  }

  // Reschedule requested by patient
  if (resource.confirmation_status === 'reschedule_requested') {
    return (
      <ActionButtonsRow>
        <Button variant="contained" color="primary" onClick={handleApprove} size="small">
          Approve Reschedule
        </Button>
        <Button variant="outlined" color="primary" onClick={handleDeny} size="small">
          Deny Request
        </Button>
        <Button variant="text" color="primary" onClick={handleContact} size="small">
          Contact Patient
        </Button>
      </ActionButtonsRow>
    );
  }

  // Cancelled by patient
  if (resource.confirmation_status === 'cancelled') {
    return (
      <ActionButtonsRow>
        <Button variant="contained" color="primary" onClick={handleAcknowledge} size="small">
          Acknowledge
        </Button>
        <Button variant="outlined" color="primary" onClick={handleRebook} size="small">
          Re-book Appointment
        </Button>
      </ActionButtonsRow>
    );
  }

  // Fallback - no buttons (shouldn't happen for actionable events)
  return null;
};
